import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink } from 'react-router-dom';
import {
  LayoutGrid,
  TrendingUp,
  Star,
  Wallet,
  Calculator,
  Check,
  User,
  PanelLeft,
  X,
  Briefcase,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useMe } from '@shared/hooks/use-me';

const NAV = [
  {
    to: '/associate/transactions',
    label: 'Transactions',
    icon: <LayoutGrid className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/associate/analytics',
    label: 'Analytics',
    icon: <TrendingUp className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/associate/services',
    label: 'Services',
    icon: <Briefcase className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/associate/wallet',
    label: 'Document Wallet',
    icon: <Wallet className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/associate/emi-calculator',
    label: 'EMI Calculator',
    icon: <Calculator className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/associate/kyc',
    label: 'KYC',
    icon: <Check className="w-[18px] h-[18px]" strokeWidth={2} />,
  },
  {
    to: '/associate/reviews',
    label: 'Reviews',
    icon: <Star className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/associate/profile',
    label: 'Profile',
    icon: <User className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
];

export interface AssociateSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function AssociateSidebar({
  isOpen,
  onClose,
  isCollapsed,
  onToggleCollapse,
}: AssociateSidebarProps) {
  const { data: me } = useMe();
  const [hoveredNav, setHoveredNav] = useState<{
    label: string;
    rect: DOMRect;
  } | null>(null);

  useEffect(() => {
    const handleClose = () => setHoveredNav(null);
    window.addEventListener('resize', handleClose);
    window.addEventListener('scroll', handleClose, true);
    return () => {
      window.removeEventListener('resize', handleClose);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, []);

  useEffect(() => {
    if (!isCollapsed) setHoveredNav(null);
  }, [isCollapsed]);

  const renderUserCard = (collapsed = false) =>
    me ? (
      <div className={cn('mt-auto border-t border-white/10', collapsed ? 'p-2' : 'p-3.5')}>
        {collapsed ? (
          <div
            onMouseEnter={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredNav({
                label:
                  [me.firstName, me.lastName].filter(Boolean).join(' ') ||
                  me.email ||
                  me.mobile ||
                  'Associate Account',
                rect,
              });
            }}
            onMouseLeave={() => setHoveredNav(null)}
            className="flex h-9 w-9 mx-auto items-center justify-center rounded-full bg-white text-brand-900 font-extrabold text-xs shadow-xs cursor-pointer"
          >
            {(me.firstName?.[0] || me.email?.[0] || me.mobile?.[0] || 'A').toUpperCase()}
          </div>
        ) : (
          <div className="flex items-center gap-2.5 rounded-xl bg-white/10 px-3 py-2.5 backdrop-blur-xs border border-white/10">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-brand-900 font-extrabold text-xs shadow-xs">
              {(me.firstName?.[0] || me.email?.[0] || me.mobile?.[0] || 'A').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-white">
                {[me.firstName, me.lastName].filter(Boolean).join(' ') ||
                  me.email ||
                  me.mobile ||
                  'Associate'}
              </p>
              <p className="text-[10px] text-brand-100 font-semibold tracking-wide">
                Associate Account
              </p>
            </div>
          </div>
        )}
      </div>
    ) : null;

  const renderNav = (onClick?: () => void, collapsed = false) => (
    <nav
      className={cn(
        'mt-5 flex flex-col gap-3 pb-6 transition-all duration-300',
        collapsed ? 'px-3.5 md:px-0 md:items-center' : 'px-3.5',
      )}
    >
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={() => {
            setHoveredNav(null);
            onClick?.();
          }}
          onMouseEnter={(e) => {
            if (!collapsed) return;
            const rect = e.currentTarget.getBoundingClientRect();
            setHoveredNav({
              label: item.label,
              rect,
            });
          }}
          onMouseLeave={() => setHoveredNav(null)}
          className={({ isActive }) =>
            cn(
              'relative group flex items-center transition-all duration-200',
              collapsed
                ? 'gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold md:w-9 md:h-9 md:p-0 md:justify-center md:rounded-lg'
                : 'gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold',
              isActive
                ? 'bg-white text-brand-900 shadow-sm font-extrabold'
                : 'text-brand-100 hover:bg-white/10 hover:text-white',
            )
          }
        >
          <span className="shrink-0 flex items-center justify-center w-5 h-5">{item.icon}</span>
          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        onScroll={() => setHoveredNav(null)}
        className={cn(
          'hidden md:flex shrink-0 flex-col border-r border-brand-900/30 bg-gradient-to-b from-brand-800 to-teal-900 text-white h-full select-none overflow-y-auto overflow-x-hidden transition-all duration-300',
          isCollapsed ? 'w-[68px]' : 'w-56',
        )}
      >
        {/* Expanded Header */}
        {!isCollapsed ? (
          <div className="flex items-center justify-between px-3.5 py-4 border-b border-white/10 min-h-[64px]">
            <Link to="/associate/transactions" className="flex items-center gap-2.5 min-w-0">
              <img
                src="/Ecregi-logo.png"
                alt="EcRegi"
                className="h-10 w-10 object-contain drop-shadow-sm shrink-0"
              />
              <span className="text-base font-bold text-white tracking-wide shrink-0">ECREGI</span>
            </Link>
            <button
              type="button"
              onClick={onToggleCollapse}
              className="inline-flex items-center justify-center rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Close sidebar"
            >
              <PanelLeft className="w-[18px] h-[18px]" strokeWidth={1.75} />
            </button>
          </div>
        ) : (
          /* Collapsed Header: EcRegi logo shown by default; on hover transitions into toggle open icon */
          <div className="flex flex-col items-center justify-center py-3.5 border-b border-white/10 min-h-[64px]">
            <button
              type="button"
              onClick={onToggleCollapse}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredNav({
                  label: 'Open sidebar',
                  rect,
                });
              }}
              onMouseLeave={() => setHoveredNav(null)}
              className="group relative flex h-11 w-11 items-center justify-center rounded-xl hover:bg-white/10 transition-all duration-200"
              aria-label="Open sidebar"
            >
              {/* Default: EcRegi logo (large, prominent, exact logo without white box) */}
              <img
                src="/Ecregi-logo.png"
                alt="EcRegi"
                className="h-10 w-10 object-contain drop-shadow-sm transition-all duration-200 group-hover:opacity-0 group-hover:scale-75"
              />

              {/* On hover: Toggle sidebar open icon */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 rounded-xl bg-white/15">
                <PanelLeft className="w-5 h-5 text-white drop-shadow-xs" strokeWidth={1.75} />
              </div>
            </button>
          </div>
        )}

        {renderNav(undefined, isCollapsed)}
        {renderUserCard(isCollapsed)}
      </aside>

      {/* Mobile Sidebar Drawer */}
      {isOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/40 transition-opacity" onClick={onClose} />
          {/* Drawer Content */}
          <aside className="relative flex w-64 max-w-xs flex-1 flex-col bg-gradient-to-b from-brand-800 to-teal-900 text-white py-4 shadow-xl select-none">
            {/* Close button */}
            <div className="absolute right-2 top-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-white/80 hover:bg-white/10"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>
            <Link
              to="/associate/transactions"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 border-b border-white/10"
            >
              <img
                src="/Ecregi-logo.png"
                alt="EcRegi"
                className="h-9 w-9 object-contain drop-shadow-sm"
              />
              <span className="text-lg font-bold text-white tracking-wide">ECREGI</span>
              <span className="inline-flex items-center gap-1 rounded bg-white/20 text-white border border-white/30 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide ml-0.5 shadow-xs">
                <Briefcase className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />
                Associate
              </span>
            </Link>
            {renderNav(onClose, false)}
            {renderUserCard(false)}
          </aside>
        </div>
      )}

      {/* Floating Tooltip Portal for collapsed sidebar (high-contrast dark tooltip for clear visibility) */}
      {isCollapsed && hoveredNav && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            top: hoveredNav.rect.top + hoveredNav.rect.height / 2,
            left: hoveredNav.rect.right + 10,
          }}
          className="fixed z-[9999] -translate-y-1/2 pointer-events-none flex items-center select-none filter drop-shadow-lg transition-opacity duration-150"
        >
          {/* Left-pointing arrow pointer notch */}
          <svg
            className="absolute -left-2 top-1/2 -translate-y-1/2 text-gray-900"
            width="8"
            height="12"
            viewBox="0 0 8 12"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0 6L8 0V12L0 6Z" />
          </svg>

          {/* Tooltip content bubble */}
          <div className="relative flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white whitespace-nowrap shadow-2xl border border-white/10">
            <span>{hoveredNav.label}</span>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
